// Sentence parsing + dictation helpers, ported from viewmodel.py.

export type Pair = { eng: string; kor: string };

/**
 * Parse pasted/loaded lines into (english, korean) pairs.
 * Accepted per line:  "English | 한국어",  "English<TAB>한국어",  "English"
 * Empty lines and lines starting with # are skipped.
 */
export function parseSentenceLines(text: string): Pair[] {
  const pairs: Pair[] = [];
  for (const raw of text.split(/\r?\n/)) {
    // strip whitespace and a leading BOM
    const line = raw.trim().replace(/^﻿/, '');
    if (!line || line.startsWith('#')) continue;

    let eng = line;
    let kor = '';
    if (line.includes('|')) {
      const idx = line.indexOf('|');
      eng = line.slice(0, idx);
      kor = line.slice(idx + 1);
    } else if (line.includes('\t')) {
      const idx = line.indexOf('\t');
      eng = line.slice(0, idx);
      kor = line.slice(idx + 1);
    }
    eng = eng.trim();
    kor = kor.trim();
    if (eng) pairs.push({ eng, kor });
  }
  return pairs;
}

const SPEAKER_RE = /^([A-Za-z][A-Za-z.'\-]*(?:\s[A-Za-z][A-Za-z.'\-]*){0,2}):\s+(.+)$/;

/**
 * Split a "NAME: dialogue" line into [name, dialogue].
 * Returns [null, text] when there is no speaker label, or the label contains a
 * digit like "SCENE 1:" (a stage direction, not a character line).
 */
export function splitSpeaker(text: string): [string | null, string] {
  const m = SPEAKER_RE.exec(text);
  if (!m) return [null, text];
  const speaker = m[1].trim();
  const rest = m[2].trim();
  if (/\d/.test(speaker)) return [null, text];
  return [speaker, rest];
}

/** Normalize for dictation comparison: lowercase, strip punctuation, collapse spaces. */
export function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Convert a two-column CSV ("eng,kor") into the "eng | kor" line format. */
export function csvToLines(content: string): string {
  const out: string[] = [];
  for (const line of content.split(/\r?\n/)) {
    if (!line.includes('|') && !line.includes('\t') && (line.match(/,/g) || []).length === 1) {
      const idx = line.indexOf(',');
      out.push(`${line.slice(0, idx).trim()} | ${line.slice(idx + 1).trim()}`);
    } else {
      out.push(line);
    }
  }
  return out.join('\n');
}

export function fmtTime(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const mm = Math.floor(s / 60)
    .toString()
    .padStart(2, '0');
  const ss = (s % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}
